import type { CameraParams, ViewKey } from '@/lib/universe/views'

import { ensureMotion, gsap, queueScrollRefresh } from '@/lib/universe/motion'

import {
    CAMERA_KEYS,
    MENU_VIEW,
    VIEWS,
    viewForPath
} from '@/lib/universe/views'

export type FallEdge = 'top' | 'bottom'

export type TransitionPhase = 'idle' | 'leaving' | 'entering'

export interface UniverseFrame {
    params: CameraParams
    reveal: number
    boost: number
    tilt: number
    time: number
    width: number
    height: number
    scrollY: number
    pointerX: number
    pointerY: number
    pointerPull: number
    holeX: number
    holeY: number
    fallEdge: FallEdge
    phase: TransitionPhase
    reducedMotion: boolean
    compact: boolean
}

type FrameListener = (frame: UniverseFrame, dt: number) => void

interface Spring {
    x: number
    v: number
}

interface Anchor {
    key: string
    center: number
}

// Every movement is a critically damped spring: it starts slowly and settles without overshooting
const CAMERA_OMEGA = 2.35
const LIGHT_OMEGA = 2.8
const LIGHT_KEYS: Array<keyof CameraParams> = ['disk', 'ring', 'stars', 'jet', 'exposure', 'streak']

const createParams = (): CameraParams => ({ ...VIEWS.home.base })

const springs = {} as Record<keyof CameraParams, Spring>

CAMERA_KEYS.forEach(key => {
    springs[key] = { x: key === 'distance' ? Math.log(VIEWS.home.base.distance) : VIEWS.home.base[key], v: 0 }
})

const revealSpring: Spring = { x: 0, v: 0 }
const boostSpring: Spring = { x: 0, v: 0 }
const tiltSpring: Spring = { x: 0, v: 0 }
const pullSpring: Spring = { x: 0, v: 0 }

const state = {
    view: 'home' as ViewKey,
    menu: false,
    phase: 'idle' as TransitionPhase,
    revealTarget: 1,
    revealOmega: 2.2,
    boostTarget: 0,
    tiltTarget: 0,
    pointerX: 0,
    pointerY: 0,
    pointerPullTarget: 0,
    primed: false,
    maxScroll: 1,
    reducedMotion: false
}

const anchors: Array<Anchor> = []
const listeners = new Set<FrameListener>()
const layoutListeners = new Set<() => void>()

const target = createParams()
const scratchFrom = createParams()
const scratchTo = createParams()

const frame: UniverseFrame = {
    params: createParams(),
    reveal: 0,
    boost: 0,
    tilt: 0,
    time: 0,
    width: 1280,
    height: 800,
    scrollY: 0,
    pointerX: 0,
    pointerY: 0,
    pointerPull: 0,
    holeX: 640,
    holeY: 400,
    fallEdge: 'top',
    phase: 'idle',
    reducedMotion: false,
    compact: false
}

let override: { view: ViewKey, patch: Partial<CameraParams> } | null = null
let introPatch: Partial<CameraParams> | null = null
let running = false
let globalsReady = false
let layoutQueued = false

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

const smooth = (value: number): number => value * value * (3 - 2 * value)

const assign = (out: CameraParams, base: CameraParams, patch?: Partial<CameraParams>): void => {
    for (const key of CAMERA_KEYS) {
        const value = patch ? patch[key] : undefined

        out[key] = value === undefined ? base[key] : value
    }
}

const mix = (out: CameraParams, from: CameraParams, to: CameraParams, t: number): void => {
    for (const key of CAMERA_KEYS) {
        if (key === 'distance') {
            const a = Math.log(from.distance)
            const b = Math.log(to.distance)

            out.distance = Math.exp(a + (b - a) * t)
        }
        else {
            out[key] = from[key] + (to[key] - from[key]) * t
        }
    }
}

const computeTarget = (scrollY: number, height: number, width: number): void => {
    const definition = VIEWS[state.view]
    const base = definition.base

    if (definition.anchors && anchors.length > 0) {
        const center = scrollY + height / 2
        const first = anchors[0]
        const last = anchors[anchors.length - 1]

        if (anchors.length === 1 || center <= first.center) {
            assign(target, base, definition.anchors[first.key])
        }
        else if (center >= last.center) {
            assign(target, base, definition.anchors[last.key])
        }
        else {
            let index = 0

            while (index < anchors.length - 2 && center >= anchors[index + 1].center) index++

            const from = anchors[index]
            const to = anchors[index + 1]
            const span = Math.max(1, to.center - from.center)

            assign(scratchFrom, base, definition.anchors[from.key])
            assign(scratchTo, base, definition.anchors[to.key])
            mix(target, scratchFrom, scratchTo, smooth(clamp01((center - from.center) / span)))
        }
    }
    else if (definition.end) {
        assign(scratchFrom, base)
        assign(scratchTo, base, definition.end)
        mix(target, scratchFrom, scratchTo, smooth(clamp01(scrollY / state.maxScroll)))
    }
    else {
        assign(target, base)
    }

    if (override && override.view === state.view) {
        for (const key of CAMERA_KEYS) {
            const value = override.patch[key]

            if (value !== undefined) target[key] = value
        }
    }

    if (state.menu) {
        for (const key of CAMERA_KEYS) {
            const value = MENU_VIEW[key]

            if (value !== undefined) target[key] = value
        }
    }

    // On a phone the hole stays near the middle and a little smaller, in its page's own viewpoint
    if (width < 768) {
        target.shiftX *= 0.25
        target.distance *= 1.12

        if (definition.compact && !state.menu) {
            for (const key of CAMERA_KEYS) {
                const value = definition.compact[key]

                if (value !== undefined) target[key] = value
            }
        }
    }

    // While the opening intro plays, the camera waits inside the shadow, then pulls back out to the page
    if (introPatch) {
        for (const key of CAMERA_KEYS) {
            const value = introPatch[key]

            if (value !== undefined) target[key] = value
        }
    }
}

const stepSpring = (spring: Spring, goal: number, omega: number, dt: number): void => {
    const force = omega * omega * (goal - spring.x) - 2 * omega * spring.v

    spring.v += force * dt
    spring.x += spring.v * dt
}

const snapCamera = (): void => {
    for (const key of CAMERA_KEYS) {
        springs[key].x = key === 'distance' ? Math.log(target.distance) : target[key]
        springs[key].v = 0
    }
}

export const documentOffset = (element: HTMLElement): { top: number, left: number } => {
    let top = 0
    let left = 0
    let node: HTMLElement | null = element

    while (node) {
        top += node.offsetTop
        left += node.offsetLeft
        node = node.offsetParent as HTMLElement | null
    }

    return { top, left }
}

const measureAnchors = (): void => {
    anchors.length = 0

    if (state.phase === 'leaving') return

    const nodes = document.querySelectorAll<HTMLElement>('[data-stage-current] [data-camera]')

    nodes.forEach(node => {
        const { top } = documentOffset(node)

        anchors.push({ key: node.dataset.camera || '', center: top + node.offsetHeight / 2 })
    })

    anchors.sort((a, b) => a.center - b.center)
}

const step = (dt: number): void => {
    const width = window.innerWidth
    const height = window.innerHeight
    const scrollY = window.scrollY

    computeTarget(scrollY, height, width)

    const cameraOmega = state.reducedMotion ? 4.2 : CAMERA_OMEGA

    for (const key of CAMERA_KEYS) {
        const goal = key === 'distance' ? Math.log(target.distance) : target[key]

        stepSpring(springs[key], goal, LIGHT_KEYS.includes(key) ? LIGHT_OMEGA : cameraOmega, dt)
        frame.params[key] = key === 'distance' ? Math.exp(springs.distance.x) : springs[key].x
    }

    stepSpring(revealSpring, state.revealTarget, state.revealOmega, dt)
    stepSpring(boostSpring, state.boostTarget, 4, dt)
    stepSpring(tiltSpring, state.tiltTarget, 3, dt)
    stepSpring(pullSpring, state.pointerPullTarget, 3, dt)

    const follow = Math.min(1, dt * 7)

    frame.pointerX += (state.pointerX - frame.pointerX) * follow
    frame.pointerY += (state.pointerY - frame.pointerY) * follow
    frame.pointerPull = Math.max(0, pullSpring.x)
    frame.reveal = clamp01(revealSpring.x)
    frame.boost = clamp01(boostSpring.x)
    frame.tilt = clamp01(tiltSpring.x)
    frame.time += dt
    frame.width = width
    frame.height = height
    frame.scrollY = scrollY
    frame.holeX = (frame.params.shiftX + 1) * 0.5 * width
    frame.holeY = (1 - frame.params.shiftY) * 0.5 * height
    frame.fallEdge = target.shiftY >= 0 ? 'top' : 'bottom'
    frame.phase = state.phase
    frame.reducedMotion = state.reducedMotion
    frame.compact = width < 768
}

// Runs on GSAP's ticker, after its tweens have rendered for this frame
const tick = (_time: number, deltaMs: number): void => {
    if (listeners.size === 0) {
        gsap.ticker.remove(tick)
        running = false

        return
    }

    const dt = Math.min(0.05, Math.max(0.001, deltaMs / 1000))

    step(dt)
    listeners.forEach(listener => listener(frame, dt))
}

const setupGlobals = (): void => {
    if (globalsReady || typeof window === 'undefined') return

    globalsReady = true

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    state.reducedMotion = motionQuery.matches
    motionQuery.addEventListener('change', event => {
        state.reducedMotion = event.matches
    })

    window.addEventListener('resize', refreshLayout, { passive: true })

    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => refreshLayout())

        observer.observe(document.body)
    }

    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => refreshLayout()).catch(() => undefined)
    }
}

const ensureTicker = (): void => {
    if (running || typeof window === 'undefined') return

    ensureMotion()
    setupGlobals()
    running = true
    gsap.ticker.add(tick)
}

export const subscribeFrame = (listener: FrameListener): (() => void) => {
    listeners.add(listener)
    ensureTicker()

    return () => {
        listeners.delete(listener)
    }
}

export const onLayoutRefresh = (listener: () => void): (() => void) => {
    layoutListeners.add(listener)

    return () => {
        layoutListeners.delete(listener)
    }
}

export function refreshLayout(): void {
    if (typeof window === 'undefined' || layoutQueued) return

    layoutQueued = true
    window.requestAnimationFrame(() => {
        layoutQueued = false
        measureAnchors()
        state.maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
        layoutListeners.forEach(listener => listener())
        queueScrollRefresh()
    })
}

export const getMaxScroll = (): number => state.maxScroll

export const getFrame = (): UniverseFrame => frame

export const getPhase = (): TransitionPhase => state.phase

export const isMenuOpen = (): boolean => state.menu

export const primeUniverse = (path: string): void => {
    if (state.primed) return

    state.primed = true
    state.view = viewForPath(path)

    if (typeof window !== 'undefined') {
        state.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        frame.width = window.innerWidth
        frame.height = window.innerHeight
    }

    computeTarget(0, frame.height, frame.width)
    snapCamera()

    for (const key of CAMERA_KEYS) {
        frame.params[key] = key === 'distance' ? Math.exp(springs.distance.x) : springs[key].x
    }

    frame.holeX = (frame.params.shiftX + 1) * 0.5 * frame.width
    frame.holeY = (1 - frame.params.shiftY) * 0.5 * frame.height
    frame.fallEdge = target.shiftY >= 0 ? 'top' : 'bottom'

    revealSpring.x = 0
    revealSpring.v = 0
    state.revealTarget = introPatch ? 0 : 1
    state.revealOmega = 2.1
    state.phase = 'entering'
    refreshLayout()
}

export const beginLeave = (path: string): void => {
    state.view = viewForPath(path)
    state.phase = 'leaving'
    state.revealTarget = 0
    state.revealOmega = state.reducedMotion ? 10 : 7
    state.boostTarget = 0
    state.tiltTarget = 0
    anchors.length = 0
}

export const beginEnter = (path: string): void => {
    state.view = viewForPath(path)
    state.phase = 'entering'
    state.revealTarget = 1
    state.revealOmega = state.reducedMotion ? 6 : 3.1
    refreshLayout()
}

export const finishEnter = (): void => {
    state.phase = 'idle'
    refreshLayout()
}

export const cancelLeave = (path: string): void => {
    state.view = viewForPath(path)
    state.phase = 'idle'
    state.revealTarget = 1
    state.revealOmega = 4
    refreshLayout()
}

export const setMenuOpen = (open: boolean): void => {
    state.menu = open
}

// A page can steer the camera directly (the catalogue zooms through its chart). It only applies
// while that page's view is the current one, so leaving the page releases it on its own.
export const setCameraOverride = (view: ViewKey, patch: Partial<CameraParams> | null): void => {
    override = patch ? { view, patch } : null
}

// The opening intro: call beginIntro before the universe is primed so the camera starts at the
// patch with the disk swallowed, and endIntro when the intro hands over to the page
export const beginIntro = (patch: Partial<CameraParams>): void => {
    introPatch = patch
    state.revealTarget = 0
}

export const endIntro = (): void => {
    if (!introPatch) return

    introPatch = null
    state.revealTarget = 1
    state.revealOmega = 1.7
    refreshLayout()
}

export const isIntroPlaying = (): boolean => introPatch !== null

export const setBoost = (value: number): void => {
    state.boostTarget = value
}

export const setTilt = (value: number): void => {
    state.tiltTarget = value
}

export const setPointer = (clientX: number, clientY: number): void => {
    const width = window.innerWidth
    const height = window.innerHeight

    state.pointerX = (2 * clientX - width) / height
    state.pointerY = (height - 2 * clientY) / height
    state.pointerPullTarget = 1
}

export const releasePointer = (): void => {
    state.pointerPullTarget = 0
}
